import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Edit, 
  ArrowLeft, 
  Calendar,
  DollarSign,
  Target,
  Users,
  MapPin,
  Tag,
  BarChart3,
  MessageSquare,
  Image,
  TrendingUp,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye,
  Download,
  Share,
  MoreHorizontal
} from 'lucide-react';
import { campaignMasterService } from '../../services/campaignMasterService';
import { formatRupeeAmount } from '../../utils/currencyFormat';
import useToast from '../../hooks/useToast';

const CampaignMasterView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadCampaign();
  }, [id]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const campaignData = await campaignMasterService.getCampaignById(id);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Error loading campaign:', error);
      error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusConfig = {
      'Draft': { icon: Clock, color: 'text-gray-500' },
      'Active': { icon: Play, color: 'text-green-500' },
      'Paused': { icon: Pause, color: 'text-yellow-500' },
      'Completed': { icon: CheckCircle, color: 'text-blue-500' },
      'Cancelled': { icon: AlertCircle, color: 'text-red-500' }
    };
    const config = statusConfig[status] || statusConfig['Draft'];
    const IconComponent = config.icon;
    return <IconComponent size={20} className={config.color} />;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Active': 'bg-green-100 text-green-800',
      'Paused': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-blue-100 text-blue-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || statusColors['Draft'];
  };

  const getTypeColor = (type) => {
    const typeColors = {
      'Email': 'bg-blue-100 text-blue-800',
      'Social Media': 'bg-purple-100 text-purple-800',
      'Digital': 'bg-green-100 text-green-800',
      'Traditional': 'bg-orange-100 text-orange-800',
      'Content': 'bg-pink-100 text-pink-800',
      'Event': 'bg-indigo-100 text-indigo-800',
      'Webinar': 'bg-cyan-100 text-cyan-800',
      'Retargeting': 'bg-red-100 text-red-800',
      'Influencer': 'bg-yellow-100 text-yellow-800',
      'Partnership': 'bg-teal-100 text-teal-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return typeColors[type] || typeColors['Other'];
  };

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <div className="title-section">
              <h1>Campaign Details</h1>
            </div>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <div className="title-section">
              <h1>Campaign Not Found</h1>
            </div>
          </div>
        </div>
        <div className="error-container">
          <p>Campaign not found or you don't have permission to view it.</p>
          <button
            className="btn-primary"
            onClick={() => navigate('/crm/campaign-master')}
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="title-section">
            <button
              className="back-button"
              onClick={() => navigate('/crm/campaign-master')}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1>{campaign.campaignName}</h1>
              <p>{campaign.campaignCode} • {campaign.campaignType} Campaign</p>
            </div>
          </div>
          <div className="title-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate('/crm/campaign-master')}
            >
              <ArrowLeft size={16} />
              Back to Campaigns
            </button>
            <button
              className="btn-primary"
              onClick={() => navigate(`/crm/campaign-master/edit/${id}`)}
            >
              <Edit size={16} />
              Edit Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Header Info */}
      <div className="campaign-header">
        <div className="campaign-status">
          <div className="status-badge">
            {getStatusIcon(campaign.campaignStatus)}
            <span className={`status-text ${getStatusColor(campaign.campaignStatus)}`}>
              {campaign.campaignStatus}
            </span>
          </div>
          <div className="campaign-meta">
            <span className={`type-badge ${getTypeColor(campaign.campaignType)}`}>
              {campaign.campaignType}
            </span>
            <span className="priority-badge">
              Priority: {campaign.campaignPriority}
            </span>
          </div>
        </div>
        <div className="campaign-dates">
          <div className="date-item">
            <Calendar size={16} className="text-gray-500" />
            <span>Start: {new Date(campaign.startDate).toLocaleDateString()}</span>
          </div>
          <div className="date-item">
            <Calendar size={16} className="text-gray-500" />
            <span>End: {new Date(campaign.endDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'targeting' ? 'active' : ''}`}
          onClick={() => setActiveTab('targeting')}
        >
          <Target size={16} />
          Targeting
        </button>
        <button
          className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <MessageSquare size={16} />
          Content
        </button>
        <button
          className={`tab-button ${activeTab === 'channels' ? 'active' : ''}`}
          onClick={() => setActiveTab('channels')}
        >
          <BarChart3 size={16} />
          Channels
        </button>
        <button
          className={`tab-button ${activeTab === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveTab('assets')}
        >
          <Image size={16} />
          Assets
        </button>
        <button
          className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => setActiveTab('team')}
        >
          <Users size={16} />
          Team
        </button>
        <button
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <TrendingUp size={16} />
          Analytics
        </button>
      </div>

      {/* Tab Content */}
      <div className="campaign-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="overview-grid">
              {/* Basic Information */}
              <div className="info-card">
                <h3>Campaign Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Campaign Code</label>
                    <span>{campaign.campaignCode}</span>
                  </div>
                  <div className="info-item">
                    <label>Campaign Name</label>
                    <span>{campaign.campaignName}</span>
                  </div>
                  <div className="info-item">
                    <label>Campaign Type</label>
                    <span className={`type-badge ${getTypeColor(campaign.campaignType)}`}>
                      {campaign.campaignType}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Campaign Category</label>
                    <span>{campaign.campaignCategory}</span>
                  </div>
                  <div className="info-item">
                    <label>Campaign Status</label>
                    <span className={`status-badge ${getStatusColor(campaign.campaignStatus)}`}>
                      {campaign.campaignStatus}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Campaign Priority</label>
                    <span>{campaign.campaignPriority}</span>
                  </div>
                </div>
              </div>

              {/* Budget & Performance */}
              <div className="info-card">
                <h3>Budget & Performance</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Budget</label>
                    <span>{formatRupeeAmount(campaign.budget || 0)}</span>
                  </div>
                  <div className="info-item">
                    <label>Actual Cost</label>
                    <span>{formatRupeeAmount(campaign.actualCost || 0)}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Leads</label>
                    <span>{campaign.totalLeads || 0}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Conversions</label>
                    <span>{campaign.totalConversions || 0}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Revenue</label>
                    <span>{formatRupeeAmount(campaign.totalRevenue || 0)}</span>
                  </div>
                  <div className="info-item">
                    <label>ROI</label>
                    <span className={campaign.returnOnInvestment > 0 ? 'text-green-600' : 'text-red-600'}>
                      {campaign.returnOnInvestment?.toFixed(1) || '0'}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Campaign Description */}
              {campaign.campaignDescription && (
                <div className="info-card full-width">
                  <h3>Campaign Description</h3>
                  <p>{campaign.campaignDescription}</p>
                </div>
              )}

              {/* Campaign Objective */}
              {campaign.campaignObjective && (
                <div className="info-card full-width">
                  <h3>Campaign Objective</h3>
                  <p>{campaign.campaignObjective}</p>
                </div>
              )}

              {/* Success Metrics */}
              {campaign.successMetrics && (
                <div className="info-card full-width">
                  <h3>Success Metrics</h3>
                  <p>{campaign.successMetrics}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Targeting Tab */}
        {activeTab === 'targeting' && (
          <div className="tab-content">
            <div className="targeting-grid">
              {/* Target Company Size */}
              <div className="info-card">
                <h3>Target Company Size</h3>
                <p>{campaign.targetCompanySize}</p>
              </div>

              {/* Target Regions */}
              {campaign.targetRegions?.length > 0 && (
                <div className="info-card">
                  <h3>Target Regions</h3>
                  <div className="tag-list">
                    {campaign.targetRegions.map((region, index) => (
                      <span key={index} className="tag">{region}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Industries */}
              {campaign.targetIndustries?.length > 0 && (
                <div className="info-card">
                  <h3>Target Industries</h3>
                  <div className="tag-list">
                    {campaign.targetIndustries.map((industry, index) => (
                      <span key={index} className="tag">{industry}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Job Titles */}
              {campaign.targetJobTitles?.length > 0 && (
                <div className="info-card">
                  <h3>Target Job Titles</h3>
                  <div className="tag-list">
                    {campaign.targetJobTitles.map((jobTitle, index) => (
                      <span key={index} className="tag">{jobTitle}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Keywords */}
              {campaign.targetKeywords?.length > 0 && (
                <div className="info-card">
                  <h3>Target Keywords</h3>
                  <div className="tag-list">
                    {campaign.targetKeywords.map((keyword, index) => (
                      <span key={index} className="tag">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Interests */}
              {campaign.interests?.length > 0 && (
                <div className="info-card">
                  <h3>Interests</h3>
                  <div className="tag-list">
                    {campaign.interests.map((interest, index) => (
                      <span key={index} className="tag">{interest}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="tab-content">
            <div className="content-grid">
              {/* Campaign Message */}
              {campaign.campaignMessage && (
                <div className="info-card">
                  <h3>Campaign Message</h3>
                  <p>{campaign.campaignMessage}</p>
                </div>
              )}

              {/* Call to Action */}
              {campaign.callToAction && (
                <div className="info-card">
                  <h3>Call to Action</h3>
                  <p>{campaign.callToAction}</p>
                </div>
              )}

              {/* Key Messages */}
              {campaign.keyMessages?.length > 0 && (
                <div className="info-card">
                  <h3>Key Messages</h3>
                  <ul className="message-list">
                    {campaign.keyMessages.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Brand Guidelines */}
              {campaign.brandGuidelines && (
                <div className="info-card">
                  <h3>Brand Guidelines</h3>
                  <p>{campaign.brandGuidelines}</p>
                </div>
              )}

              {/* Tone of Voice */}
              <div className="info-card">
                <h3>Tone of Voice</h3>
                <p>{campaign.toneOfVoice}</p>
              </div>
            </div>
          </div>
        )}

        {/* Channels Tab */}
        {activeTab === 'channels' && (
          <div className="tab-content">
            <div className="channels-grid">
              {/* Marketing Channels */}
              {campaign.channels?.length > 0 && (
                <div className="info-card full-width">
                  <h3>Marketing Channels</h3>
                  <div className="channels-list">
                    {campaign.channels.map((channel, index) => (
                      <div key={index} className="channel-item">
                        <div className="channel-info">
                          <h4>{channel.channelName}</h4>
                          <span className={`status-badge ${getStatusColor(channel.channelStatus)}`}>
                            {channel.channelStatus}
                          </span>
                        </div>
                        <div className="channel-metrics">
                          <div className="metric">
                            <label>Budget</label>
                            <span>{formatRupeeAmount(channel.channelBudget || 0)}</span>
                          </div>
                          <div className="metric">
                            <label>Spent</label>
                            <span>{formatRupeeAmount(channel.channelSpent || 0)}</span>
                          </div>
                          <div className="metric">
                            <label>Impressions</label>
                            <span>{channel.channelImpressions?.toLocaleString() || '0'}</span>
                          </div>
                          <div className="metric">
                            <label>Clicks</label>
                            <span>{channel.channelClicks?.toLocaleString() || '0'}</span>
                          </div>
                          <div className="metric">
                            <label>Conversions</label>
                            <span>{channel.channelConversions?.toLocaleString() || '0'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Landing Page & Tracking */}
              <div className="info-card">
                <h3>Landing Page & Tracking</h3>
                <div className="info-grid">
                  {campaign.landingPageUrl && (
                    <div className="info-item">
                      <label>Landing Page URL</label>
                      <a href={campaign.landingPageUrl} target="_blank" rel="noopener noreferrer" className="link">
                        {campaign.landingPageUrl}
                      </a>
                    </div>
                  )}
                  {campaign.trackingCode && (
                    <div className="info-item">
                      <label>Tracking Code</label>
                      <span className="code">{campaign.trackingCode}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assets Tab */}
        {activeTab === 'assets' && (
          <div className="tab-content">
            <div className="assets-grid">
              {/* Creative Assets */}
              {campaign.creativeAssets?.length > 0 && (
                <div className="info-card full-width">
                  <h3>Creative Assets</h3>
                  <div className="assets-list">
                    {campaign.creativeAssets.map((asset, index) => (
                      <div key={index} className="asset-item">
                        <div className="asset-info">
                          <h4>{asset.assetName}</h4>
                          <span className="asset-type">{asset.assetType}</span>
                          {asset.isPrimary && <span className="primary-badge">Primary</span>}
                        </div>
                        {asset.assetUrl && (
                          <a href={asset.assetUrl} target="_blank" rel="noopener noreferrer" className="asset-link">
                            View Asset
                          </a>
                        )}
                        {asset.assetDescription && (
                          <p className="asset-description">{asset.assetDescription}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className="tab-content">
            <div className="team-grid">
              {/* Team Members */}
              {campaign.teamMembers?.length > 0 && (
                <div className="info-card">
                  <h3>Team Members</h3>
                  <div className="team-list">
                    {campaign.teamMembers.map((member, index) => (
                      <div key={index} className="team-member">
                        <div className="member-info">
                          <h4>{member.user?.firstName} {member.user?.lastName}</h4>
                          <span className="member-role">{member.role}</span>
                        </div>
                        <div className="member-email">{member.user?.email}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stakeholders */}
              {campaign.stakeholders?.length > 0 && (
                <div className="info-card">
                  <h3>Stakeholders</h3>
                  <div className="stakeholders-list">
                    {campaign.stakeholders.map((stakeholder, index) => (
                      <div key={index} className="stakeholder">
                        <div className="stakeholder-info">
                          <h4>{stakeholder.name}</h4>
                          <span className="stakeholder-role">{stakeholder.role}</span>
                        </div>
                        <div className="stakeholder-details">
                          <div className="stakeholder-email">{stakeholder.email}</div>
                          <div className="stakeholder-department">{stakeholder.department}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="tab-content">
            <div className="analytics-grid">
              {/* Performance Metrics */}
              <div className="info-card">
                <h3>Performance Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <label>Total Impressions</label>
                    <span className="metric-value">{campaign.totalImpressions?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="metric-card">
                    <label>Total Clicks</label>
                    <span className="metric-value">{campaign.totalClicks?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="metric-card">
                    <label>Click Through Rate</label>
                    <span className="metric-value">{campaign.clickThroughRate?.toFixed(2) || '0'}%</span>
                  </div>
                  <div className="metric-card">
                    <label>Conversion Rate</label>
                    <span className="metric-value">{campaign.conversionRate?.toFixed(2) || '0'}%</span>
                  </div>
                  <div className="metric-card">
                    <label>Cost Per Click</label>
                    <span className="metric-value">₹{campaign.costPerClick?.toFixed(2) || '0'}</span>
                  </div>
                  <div className="metric-card">
                    <label>Cost Per Lead</label>
                    <span className="metric-value">₹{campaign.costPerLead?.toFixed(2) || '0'}</span>
                  </div>
                </div>
              </div>

              {/* Quality Scores */}
              <div className="info-card">
                <h3>Quality Scores</h3>
                <div className="scores-grid">
                  <div className="score-item">
                    <label>Performance Score</label>
                    <div className="score-bar">
                      <div className="score-fill" style={{ width: `${campaign.performanceScore || 0}%` }}></div>
                      <span className="score-value">{campaign.performanceScore || 0}%</span>
                    </div>
                  </div>
                  <div className="score-item">
                    <label>Quality Score</label>
                    <div className="score-bar">
                      <div className="score-fill" style={{ width: `${(campaign.qualityScore || 0) * 10}%` }}></div>
                      <span className="score-value">{campaign.qualityScore || 0}/10</span>
                    </div>
                  </div>
                  <div className="score-item">
                    <label>Engagement Score</label>
                    <div className="score-bar">
                      <div className="score-fill" style={{ width: `${(campaign.engagementScore || 0) * 10}%` }}></div>
                      <span className="score-value">{campaign.engagementScore || 0}/10</span>
                    </div>
                  </div>
                  <div className="score-item">
                    <label>Effectiveness Score</label>
                    <div className="score-bar">
                      <div className="score-fill" style={{ width: `${(campaign.effectivenessScore || 0) * 10}%` }}></div>
                      <span className="score-value">{campaign.effectivenessScore || 0}/10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {campaign.notes && (
          <div className="info-card full-width">
            <h3>Additional Notes</h3>
            <p>{campaign.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignMasterView;
